import type {SketchConstraintState} from '../../../history/solver/SketchSolverRecordTypes';
import type {SerializablePlainData} from '../../../history/domain/SerializablePlainData';

/**
 * 读取并校验指定类型的关系分支提示对象。
 *
 * @param state 关系 solver 快照。
 * @param expectedKind 期望的分支提示类型。
 * @returns 匹配的 plain record；无提示或类型不符时返回 null。
 */
export function readSketchConstraintBranchHintRecord(
    state: SketchConstraintState,
    expectedKind: string,
): Readonly<Record<string, SerializablePlainData>> | null {
    const hint = state.branchHint;

    if (!hint || typeof hint !== 'object' || Array.isArray(hint)) {
        return null;
    }
    return Reflect.get(hint, 'kind') === expectedKind
        ? hint as Readonly<Record<string, SerializablePlainData>>
        : null;
}

/**
 * 读取分支提示中的有限数值。
 *
 * @param state 关系 solver 快照。
 * @param expectedKind 期望提示类型。
 * @param key 数值字段。
 * @returns 有限数值；不存在或非法时返回 null。
 */
export function readSketchConstraintBranchHintNumber(
    state: SketchConstraintState,
    expectedKind: string,
    key: string,
): number | null {
    const record = readSketchConstraintBranchHintRecord(state, expectedKind);
    const value = record ? Reflect.get(record, key) : null;

    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * 读取分支提示中的二维有限点。
 *
 * @param state 关系 solver 快照。
 * @param expectedKind 期望提示类型。
 * @param key 二维点字段。
 * @returns 二维点；不存在或非法时返回 null。
 */
export function readSketchConstraintBranchHintPoint(
    state: SketchConstraintState,
    expectedKind: string,
    key: string,
): readonly [number, number] | null {
    const record = readSketchConstraintBranchHintRecord(state, expectedKind);
    const value = record ? Reflect.get(record, key) : null;

    if (!Array.isArray(value) || value.length !== 2
        || typeof value[0] !== 'number' || !Number.isFinite(value[0])
        || typeof value[1] !== 'number' || !Number.isFinite(value[1])) {
        return null;
    }

    return [value[0], value[1]];
}
